import { Cart, DiscountCodeState } from 'shared/types/cart';
import { PriceDetails } from 'shared/types/checkout';
import { Money } from 'shared/types/product';

export const centsToDollarString = (
  centsAmount?: number,
  fractionDigits?: number,
) => {
  const dollarAmount = (centsAmount ?? 0) / 10 ** (fractionDigits ?? 2);
  return `$${dollarAmount.toFixed(fractionDigits)}`;
};

export const centsToDollars = ({ centAmount, fractionDigits }: Money = {}) =>
  (centAmount ?? 0) / 10 ** (fractionDigits ?? 2);

export const sumMoney = (...args: (Money | undefined)[]) => {
  const moneyArray = args.filter(Boolean);

  return {
    centAmount: moneyArray.reduce(
      (acc, { centAmount } = {}) => acc + (centAmount ?? 0),
      0,
    ),
    fractionDigits: moneyArray[0]?.fractionDigits,
    currencyCode: moneyArray[0]?.currencyCode,
  } as Money;
};

export const getPriceDetailsFromCart = (cart?: Partial<Cart>) => {
  const lineItems = cart?.lineItems ?? [];
  const lineItemsTotalPrice = lineItems.reduce(
    (sum, item) => sum + (item.price?.centAmount ?? 0) * (item.count ?? 1),
    0,
  );

  const {
    centAmount: cartTotal,
    currencyCode,
    fractionDigits,
  } = cart?.sum ?? {};

  let cartDiscount: PriceDetails['cartDiscount'] = {
    amount:
      (cart?.discountOnTotalPrice?.centAmount ?? 0) +
      (cart?.discountOnLineItems?.centAmount ?? 0),
  };

  if (cart?.discountCodes?.length) {
    const discount =
      cart.discountCodes.find(
        (disc) => disc.state === DiscountCodeState.MATCHES_CART,
      ) ?? {};

    const percent = (
      ((cartDiscount.amount ?? 0) / (lineItemsTotalPrice ?? 0)) *
      100
    ).toFixed(0);

    cartDiscount = { ...cartDiscount, discount, percent };
  }

  const lineItemsTaxed = {
    amount: sumMoney(...lineItems.map(({ taxedPrice }) => taxedPrice?.amount)),
    grossAmount: sumMoney(
      ...lineItems.map(({ taxedPrice }) => taxedPrice?.grossAmount),
    ),
  };

  const discount = sumMoney(
    cart?.discountOnTotalPrice,
    cart?.discountOnLineItems,
  );

  // Sum of all the types of discount on the cart
  const discountInDollars = centsToDollars(discount);

  // Subtract the discount from the total taxed price.
  if (
    cart?.discountOnTotalPrice?.centAmount &&
    lineItemsTaxed.amount.centAmount
  ) {
    lineItemsTaxed.amount.centAmount -= cart.discountOnTotalPrice.centAmount;
  }

  if (
    cart?.discountOnTotalPrice?.centAmount &&
    lineItemsTaxed.grossAmount.centAmount
  ) {
    lineItemsTaxed.grossAmount.centAmount -=
      cart.discountOnTotalPrice.centAmount;
  }

  return {
    lineItemsTotalPrice,
    cartTotal,
    cartDiscount,
    currencyCode,
    fractionDigits,
    lineItemsTaxed,
    discountInDollars,
  } as PriceDetails;
};
